import { createFileRoute, Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { LegalLayout } from "@/components/legal/legal-layout";
import { SELLER } from "@/content/legal";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Notice | Smart Word Labs" },
      {
        name: "description",
        content:
          "How Smart Word Labs collects, uses, shares and protects personal data for parents and children using our online English lessons.",
      },
      { property: "og:title", content: "Privacy Notice | Smart Word Labs" },
      {
        property: "og:description",
        content: "How we handle parent and child personal data at Smart Word Labs.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}/privacy` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Privacy Notice", path: "/privacy" }]),
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { t } = useTranslation("legal");
  const dataItems = t("privacy.dataCollected.items", { returnObjects: true }) as string[];
  const shareItems = t("privacy.shareWith.items", { returnObjects: true }) as string[];

  return (
    <LegalLayout title={t("privacy.title")} updated={t("privacy.updated")} path="/privacy">
      <p>
        <Trans
          i18nKey="privacy.intro"
          ns="legal"
          values={{ tradingName: SELLER.tradingName }}
          components={{ strong: <strong /> }}
        />
      </p>

      <h2>{t("privacy.dataCollected.heading")}</h2>
      <ul>
        {dataItems.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>
      <p>{t("privacy.dataCollected.noChildData")}</p>

      <h2>{t("privacy.shareWith.heading")}</h2>
      <ul>
        {shareItems.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>
      <p>{t("privacy.shareWith.noSell")}</p>

      <h2>{t("privacy.transfers.heading")}</h2>
      <p>{t("privacy.transfers.body")}</p>

      <h2>{t("privacy.retention.heading")}</h2>
      <p>{t("privacy.retention.body")}</p>

      <h2>{t("privacy.rights.heading")}</h2>
      <p>
        <Trans
          i18nKey="privacy.rights.body"
          ns="legal"
          components={[<Link to="/contact" key="0" />]}
        />
      </p>

      <h2>{t("privacy.security.heading")}</h2>
      <p dangerouslySetInnerHTML={{ __html: t("privacy.security.body") }} />

      <h2>{t("privacy.cookies.heading")}</h2>
      <p>{t("privacy.cookies.body")}</p>

      <h2>{t("privacy.contact.heading")}</h2>
      <p>
        <Trans
          i18nKey="privacy.contact.body"
          ns="legal"
          components={[<Link to="/contact" key="0" />]}
        />
      </p>
    </LegalLayout>
  );
}
